import React, { memo, useCallback } from "react";
import { useApproveUpcomingUserMutation } from "@/graphql/generated";
import { Button } from "../ui/button";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCheck } from "lucide-react";
import { toaster } from "@/utils/toaster";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useDispatch } from "react-redux";
import { setUpdatePatientLine } from "@/context/features/room/roomSlice";

type Props = {
  userId: string;
  toastId?: string | number;
  isToast?: boolean;
  onApproved?: (userId: string) => void;
};

function OpenPatient({ userId, toastId, isToast = true, onApproved }: Props) {
  const { t } = useTranslation();
  const [approveUpcomingUser, { loading }] = useApproveUpcomingUserMutation();
  const dispatch = useDispatch();

  const handleApprove = useCallback(
    async (e?: React.MouseEvent | React.PointerEvent) => {
      e?.stopPropagation();
      try {
        await approveUpcomingUser({ variables: { userId } });
        if (toastId !== undefined) toast.dismiss(toastId);
        toaster("success", t("user_approved") + ".");
        onApproved?.(userId);
        dispatch(setUpdatePatientLine(true));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        // console.log(err.message);
        toaster("error", t(err.message) + ".");
      }
    },
    [approveUpcomingUser, userId, toastId, onApproved, t, dispatch]
  );

  if (isToast) {
    return (
      <div className="absolute end-1 bottom-1">
        <Button
          variant="link"
          onClick={handleApprove}
          onPointerDown={(e) => e.stopPropagation()}
          className="ml-auto text-sm font-medium text-primary underline underline-offset-4"
          disabled={loading}
        >
          {loading ? t("approving") : t("approve_user")}
        </Button>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          className="w-8 h-8 p-1"
          onClick={handleApprove}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label={t("approve_user")}
          title={t("approve_user")}
          disabled={loading}
        >
          <CheckCheck size={20} strokeWidth={1.5} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{t("approve")}</TooltipContent>
    </Tooltip>
  );
}

export default memo(OpenPatient);
